import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { ExportTargetsEnum, TImportTargets } from "../types";
import type { TJobMetadata } from "./jobsStore";
import { ProcessingJobStatus } from "./jobsStore";
import type { TFetchStatus, TParamsPagination } from "./types";

export const fetchImportedFiles = createAsyncThunk(
    "filesHistory/importedFiles",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/history/imports", {
                params: options,
            });
            return response.data.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchExportedFiles = createAsyncThunk(
    "filesHistory/exportedFiles",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/history/exports", {
                params: options,
            });
            return response.data.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchExportedFile = createAsyncThunk(
    "fileHistory/exportedFile",
    async ({ id, filename, isBlob }: { id: number; filename?: string; isBlob?: boolean }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/history/exports/${id}`, {
                    headers: { Accept: "*/*" },
                    responseType: isBlob ? "blob" : "json",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const extension = () => {
                switch (response.data.target) {
                    case "bankPayment":
                        return "xml";
                    case "URSSAF":
                    case "DAS2":
                        return "txt";
                    case "terms":
                    case "sales":
                        return "csv";
                    default:
                        return "xlsx";
                }
            };

            const mimeType = () => {
                switch (response.data.target) {
                    case "bankPayment":
                        return "application/xml";
                    case "URSSAF":
                    case "DAS2":
                        return "text/plain";
                    case "terms":
                    case "sales":
                        return "text/csv";
                    default:
                        return response.headers["Content-Type"] ?? "application/vnd.openxmlformats";
                }
            };

            const fileName = filename ?? `export_${response.data.target}.${extension()}`;
            if (response.data?.url) {
                downloadFile({ source: response.data.url, fileName, isLink: true });
                return;
            }
            const linkSource = response.data?.file ? `data:${mimeType()};base64,${response.data.file}` : response.data;

            downloadFile({ source: linkSource, fileName, isLink: !isBlob });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchImportedFile = createAsyncThunk(
    "fileHistory/importedFile",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/${id}`, {
                    responseType: "blob",
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            // Determine file type from Content-Type header
            const contentType = response.headers["content-type"] || "";
            let mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            if (contentType.includes("text/csv") || contentType.includes("application/csv")) {
                mimeType = "text/csv";
            } else if (contentType.includes("application/vnd.openxmlformats")) {
                mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            }

            const contentDisposition = response.headers["content-disposition"];

            // Extract filename from Content-Disposition header and determine MIME type from extension as fallback
            let fileName = "imported-file";
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    fileName = filenameMatch[1];
                    // Use filename extension as fallback for MIME type detection
                    const fileExtension = fileName.split(".").pop()?.toLowerCase();
                    if (fileExtension === "csv") {
                        mimeType = "text/csv";
                    } else if (fileExtension === "xlsx") {
                        mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                    }
                }
            }

            const blob = new Blob([response.data], {
                type: mimeType,
            });

            downloadFile({ source: blob, fileName, isLink: false });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const rollBackFile = createAsyncThunk("fileHistory/rollbackFile", async () => {});

/**
 * Get anomaly data from async import that failed.
 */
export const fetchAnomaliesData = createAsyncThunk(
    "fileHistory/fetchAnomalyData",
    async ({ importId }: { importId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/${importId}/anomaly`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/*
 * Get anomaly report as a file (csv or xlsx)
 */
export const fetchAnomaliesReportAsFile = createAsyncThunk(
    "fileHistory/fetchAnomaliesReportAsFile",
    async ({ importId }: { importId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/${importId}/anomaly-file`, {
                    responseType: "blob",
                    headers: {
                        Accept: "*/*",
                    },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            // Determine file type from Content-Type header
            const contentType = response.headers["content-type"] || "";
            let fileExtension = "xlsx";
            let mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            if (contentType.includes("text/csv") || contentType.includes("application/csv")) {
                fileExtension = "csv";
                mimeType = "text/csv";
            } else if (contentType.includes("application/vnd.openxmlformats")) {
                fileExtension = "xlsx";
                mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            }

            // Check Content-Disposition header for filename as fallback
            const contentDisposition = response.headers["content-disposition"];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    const headerFileName = filenameMatch[1];
                    const headerExtension = headerFileName.split(".").pop()?.toLowerCase();
                    if (headerExtension === "csv" || headerExtension === "xlsx") {
                        fileExtension = headerExtension;
                        mimeType =
                            headerExtension === "csv"
                                ? "text/csv"
                                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                    }
                }
            }

            const blob = new Blob([response.data], {
                type: mimeType,
            });

            const fileName = `anomaly-report-${importId}.${fileExtension}`;

            downloadFile({ source: blob, fileName, isLink: false });

            return { success: true, fileName };
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.message);
        }
    },
);

export const fetchImportJobOfProcessingJob = createAsyncThunk(
    "fileHistory/fetchImportJobOfProcessingJob",
    async (processingJobId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/job/${processingJobId}`,
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface IFile {
    date: Date;
    id: number;
    filename: string;
    target: TImportTargets | ExportTargetsEnum;
    user: string;
    numberOfLines: number;
    jobStatus: ProcessingJobStatus;
    data: JSON;
    companyId: number;
    processingJobId: number | null;
    metadata: TFileMetadata;
    sourceType: ImportSourceType;
    jobMetadata: TJobMetadata;
}

export type TFileMetadata = {
    revenue?: number;
    invoiceable?: number;
    iwfId: number;
    /** Channel selected at sales import. `null` means all channels. */
    channelId?: number | null;
    streamed?: boolean;
} & {
    invoiceId?: number;
    invoiceURL?: string;
    invoiceIntegrationId?: number;
};

export type ImportSourceType = "crealo" | "crealo-sftp" | "interforum-sftp";

export interface CounterState {
    exportedFileStatus: TFetchStatus;
    importedFileStatus: TFetchStatus;
    importedFilesStatus: TFetchStatus;
    exportedFilesStatus: TFetchStatus;
    importedFiles: IFile[];
    exportedFiles: IFile[];
    importedFilesCount: number;
    exportedFilesCount: number;
}

//TODO: set initial state as empty array when backend will be ready
const initialState: CounterState = {
    importedFileStatus: "idle",
    exportedFileStatus: "idle",
    importedFilesStatus: "idle",
    exportedFilesStatus: "idle",
    importedFiles: [],
    exportedFiles: [],
    importedFilesCount: 0,
    exportedFilesCount: 0,
};

export const filesHistorySlice = createSlice({
    name: "history",
    initialState,
    reducers: {
        setImportIdleStatus(state) {
            state.importedFilesStatus = "idle";
        },
        setExportIdleStatus(state) {
            state.exportedFilesStatus = "idle";
        },
        setSingleImportedFileIdleStatus(state) {
            state.importedFileStatus = "idle";
        },
        setSingleExportedFileIdleStatus(state) {
            state.exportedFileStatus = "idle";
        },
        resetImportFiles(state, { payload }) {
            state.importedFiles = state.importedFiles.map((file) => {
                if ((file as IFile).processingJobId === payload) {
                    return { ...file, jobStatus: ProcessingJobStatus.REVERTED };
                } else {
                    return file;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchImportedFiles.fulfilled, (state, { payload }) => {
            state.importedFilesStatus = "succeeded";
            const files: IFile[] = [...payload.imports];
            state.importedFiles = files.map((file) => {
                if (file.user.includes("@crealo.app")) {
                    file.user = t("History.CrealoTeam");
                }
                return file;
            });
            state.importedFilesCount = payload.count;
        });
        builder.addCase(fetchImportedFiles.pending, (state) => {
            state.importedFilesStatus = "pending";
        });
        builder.addCase(fetchImportedFiles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.importedFilesStatus = "failed";
        });

        builder.addCase(fetchExportedFiles.fulfilled, (state, { payload }) => {
            state.exportedFilesStatus = "succeeded";
            const files: IFile[] = [...payload.exports];
            state.exportedFiles = files.map((file) => {
                if (file.user.includes("@crealo.app")) {
                    file.user = t("History.CrealoTeam");
                }
                return file;
            });
            state.exportedFilesCount = payload.count;
        });
        builder.addCase(fetchExportedFiles.pending, (state) => {
            state.exportedFilesStatus = "pending";
            state.exportedFiles = [];
        });
        builder.addCase(fetchExportedFiles.rejected, (state) => {
            toast.error(`${t("History.FailedToLoadExported")}`);
            state.exportedFilesStatus = "failed";
        });

        builder.addCase(fetchImportedFile.fulfilled, (state) => {
            toast.success(`${t("History.SuccessDownloadFile")}`);
            state.importedFileStatus = "succeeded";
        });
        builder.addCase(fetchImportedFile.pending, (state) => {
            state.importedFileStatus = "pending";
        });
        builder.addCase(fetchImportedFile.rejected, (state) => {
            state.importedFileStatus = "failed";
        });

        builder.addCase(fetchExportedFile.fulfilled, (state) => {
            toast.success(`${t("History.SuccessDownloadFile")}`);
            state.exportedFileStatus = "succeeded";
        });
        builder.addCase(fetchExportedFile.pending, (state) => {
            state.exportedFileStatus = "pending";
        });
        builder.addCase(fetchExportedFile.rejected, (state) => {
            state.exportedFileStatus = "failed";
        });
    },
});

export const {
    setExportIdleStatus,
    setImportIdleStatus,
    setSingleImportedFileIdleStatus,
    setSingleExportedFileIdleStatus,
    resetImportFiles,
} = filesHistorySlice.actions;
export default filesHistorySlice.reducer;
